import { useCallback, useState } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import HideModal from 'modules/documents/components/BulkActionsDatatable/HideModal'
import ResendSignatureReminderModal from 'modules/documents/components/BulkActionsDatatable/ResendSignatureReminderModal'
import TrashBinModal from 'modules/documents/components/BulkActionsDatatable/TrashBinModal'
import UpdateFolderModal from 'modules/documents/components/BulkActionsDatatable/UpdateFolderModal'
import VisibleModal from 'modules/documents/components/BulkActionsDatatable/VisibleModal'
import useBulkUpdateFolderDocument from 'modules/documents/hooks/documents/useBulkUpdateFolderDocument'
import useBulkUpdateVisibilityDocument from 'modules/documents/hooks/documents/useBulkUpdateVisibilityDocument'
import useMoveToTrashBinDocument from 'modules/documents/hooks/documents/useMoveToTrashBinDocument'
import {
  canDeleteDocuments,
  canUpdateDocuments,
  downloadDocuments,
  isPendingSignature,
} from 'modules/documents/lib/Documents'
import { useBulkResendRequestSignature } from 'modules/esignatures/hooks/signatures/useBulkResendRequestSignature'

import type { DocumentNode } from './types'

const ACTION_IDS = {
  HIDE: 'hide',
  VISIBLE: 'visible',
  RESEND_SIGNATURE: 'resend-signature',
  MOVE_TO_FOLDER: 'move-to-folder',
  DOWNLOAD: 'download',
  MOVE_TO_TRASH: 'move-to-trash',
} as const

type ActionId = (typeof ACTION_IDS)[keyof typeof ACTION_IDS]

const isActionId = (value: string): value is ActionId => {
  const values: ReadonlyArray<string> = Object.values(ACTION_IDS)
  return values.includes(value)
}

type ItemStatus = { checked: boolean; item: DocumentNode }

export function useBulkActions() {
  const session = useCurrentSession()

  const { moveDocumentToTrashBin, loading: isMovingToTrashBin } = useMoveToTrashBinDocument({
    fromDataTable: true,
  })
  const { bulkUpdateVisibility, loading: isUpdatingVisibility } = useBulkUpdateVisibilityDocument({
    fromDataTable: true,
  })
  const { bulkResendRequestSignature, loading: isResendingSignature } =
    useBulkResendRequestSignature()
  const { bulkUpdateFolder } = useBulkUpdateFolderDocument({ fromDataTable: true })

  const [modal, setModal] = useState<ActionId | null>(null)
  const [selectedDocs, setSelectedDocs] = useState<DocumentNode[]>([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [loadingUpdateFolder, setLoadingUpdateFolder] = useState(false)

  const closeModal = () => setModal(null)

  const isMyDocument = (doc: DocumentNode) =>
    doc.employee ? session.employee?.id === doc.employee.id : false

  const bulkActions = useCallback(
    ({ itemsStatus }: { itemsStatus: readonly ItemStatus[] }) => {
      const selected = itemsStatus.filter((s) => s.checked).map((s) => s.item)
      if (selected.length === 0) return { primary: [], secondary: [] }

      const canUpdate = canUpdateDocuments(selected)
      const canDelete = canDeleteDocuments(selected)
      const hasPendingSignature = selected.some(
        (doc) => isPendingSignature(doc) && doc.status !== 'to_fill'
      )
      const canMakeVisible = selected.some((doc) => doc.public === false && !isMyDocument(doc))
      const canHide = selected.some((doc) => doc.public === true && !isMyDocument(doc))

      const primary: Array<{ id: ActionId; label: string }> = []
      const secondary: Array<{ id: ActionId; label: string }> = []

      if (canUpdate && canHide) {
        secondary.push({
          id: ACTION_IDS.HIDE,
          label: i18n.t('documents.employee.bulk-actions.hide'),
        })
      }
      if (canUpdate && canMakeVisible) {
        secondary.push({
          id: ACTION_IDS.VISIBLE,
          label: i18n.t('documents.employee.bulk-actions.visible'),
        })
      }
      if (hasPendingSignature) {
        secondary.push({
          id: ACTION_IDS.RESEND_SIGNATURE,
          label: i18n.t('documents.employee.bulk-actions.resend_signature_reminder'),
        })
      }
      if (canUpdate) {
        secondary.push({
          id: ACTION_IDS.MOVE_TO_FOLDER,
          label: i18n.t('documents.move'),
        })
      }
      secondary.push({
        id: ACTION_IDS.DOWNLOAD,
        label: i18n.t('documents.employee.bulk-actions.download'),
      })
      if (canDelete) {
        secondary.push({
          id: ACTION_IDS.MOVE_TO_TRASH,
          label: i18n.t('documents.move_to_trash_bin'),
        })
      }

      return { primary, secondary }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.employee?.id]
  )

  const handleBulkAction = useCallback(
    async (actionId: string, { itemsStatus }: { itemsStatus: readonly ItemStatus[] }) => {
      const selected = itemsStatus.filter((s) => s.checked).map((s) => s.item)
      setSelectedDocs(selected)
      const selectedIds = selected.map((d) => d.id)

      if (actionId === ACTION_IDS.DOWNLOAD) {
        setIsDownloading(true)
        try {
          const response = await downloadDocuments(selectedIds)
          const blob = new Blob([response], { type: 'application/zip' })
          downloadBlob(blob, `${i18n.t('documents.employee.title')}.zip`)
        } finally {
          setIsDownloading(false)
        }
        return
      }

      if (isActionId(actionId)) {
        setModal(actionId)
      }
    },
    []
  )

  const onMoveToTrashBin = async () => {
    const result = await moveDocumentToTrashBin({
      documentIds: selectedDocs.map((d) => d.id),
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.removed_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    closeModal()
  }

  const onUpdateVisibility = async (visible: boolean, sendNotifications: boolean) => {
    const result = await bulkUpdateVisibility({
      documentIds: selectedDocs.map((d) => d.id),
      sendNotifications,
      visibility: visible,
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    closeModal()
  }

  const onSubmitResendSignature = async () => {
    const pendingDocs = selectedDocs.filter(
      (doc) => isPendingSignature(doc) && doc.status !== 'to_fill'
    )
    const result = await bulkResendRequestSignature({
      documentIds: pendingDocs.map((d) => d.id),
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.employee.bulk-actions.resend-signature.success', {
          count: pendingDocs.length,
        }),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
    }
    closeModal()
  }

  const onSubmitUpdateFolder = async (selectedFolderId: string | null) => {
    setLoadingUpdateFolder(true)
    const result = await bulkUpdateFolder({
      documentIds: selectedDocs.map((d) => d.id),
      folderId: selectedFolderId,
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('documents.employee.bulk-actions.update-folder.success'),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
    }
    setLoadingUpdateFolder(false)
    closeModal()
  }

  const selectedDocsWithSignature = selectedDocs.filter((doc) => doc.signatureStatus !== null)
  const pendingSignatureDocs = selectedDocs.filter(
    (doc) => isPendingSignature(doc) && doc.status !== 'to_fill'
  )

  const Modals = () => (
    <>
      {modal === ACTION_IDS.MOVE_TO_TRASH && (
        <TrashBinModal
          useF0
          filename={selectedDocs[0]?.filename}
          totalDocuments={selectedDocs.length}
          onClose={closeModal}
          onSubmit={onMoveToTrashBin}
          loading={isMovingToTrashBin}
          hasSignature={selectedDocsWithSignature.length > 0}
        />
      )}
      {modal === ACTION_IDS.HIDE && (
        <HideModal
          useF0
          totalSelectedDocuments={selectedDocs.length}
          loading={isUpdatingVisibility}
          onSubmit={() => onUpdateVisibility(false, false)}
          onClose={closeModal}
        />
      )}
      {modal === ACTION_IDS.VISIBLE && (
        <VisibleModal
          useF0
          totalSelectedDocuments={selectedDocs.length}
          loading={isUpdatingVisibility}
          onSubmit={(sendNotifications: boolean) => onUpdateVisibility(true, sendNotifications)}
          onClose={closeModal}
        />
      )}
      {modal === ACTION_IDS.RESEND_SIGNATURE && (
        <ResendSignatureReminderModal
          useF0
          totalSelectedDocuments={pendingSignatureDocs.length}
          onClose={closeModal}
          onSubmit={onSubmitResendSignature}
          loading={isResendingSignature}
        />
      )}
      {modal === ACTION_IDS.MOVE_TO_FOLDER && (
        <UpdateFolderModal
          useF0
          documents={selectedDocs}
          onClose={closeModal}
          onSubmit={onSubmitUpdateFolder}
          loading={loadingUpdateFolder}
        />
      )}
    </>
  )

  return {
    bulkActions,
    handleBulkAction,
    Modals,
    isDownloading,
  }
}
